<template>
  <div
    ref="outerContainer"
    class="
      attachment-list__item
      tw-border-solid tw-border tw-border-sf-snow-dark tw-rounded-md
    "
    :class="{
      'attachment-list__item--open': singleAttachment || previewOpened,
    }"
  >
    <div
      class="
        tw-h-10 tw-pr-3 tw-flex tw-gap-3 tw-items-center tw-justify-between
      "
    >
      <div
        :title="attachment.name"
        class="
          tw-pl-3
          tw-text-sm
          tw-overflow-hidden
          tw-text-ellipsis
          tw-whitespace-nowrap
          tw-flex-1
          tw-h-full
          tw-flex
          tw-items-center
        "
        :class="{
          'tw-text-neutral-500': attachment.uploadId,
          'tw-cursor-pointer': attachment.previewable && !singleAttachment,
        }"
        @click="
          !singleAttachment &&
            attachment.previewable &&
            handleToggleAttachmentPreview(attachment)
        "
      >
        <i
          v-if="!singleAttachment && attachment.previewable"
          class="fa fa-chevron-right tw-mr-2"
        ></i>
        {{ attachment.name }}
      </div>
      <div class="tw-flex tw-items-center tw-justify-center">
        <div
          v-if="attachment.uploadProgress"
          class="tw-flex tw-items-center tw-mr-4"
        >
          <div
            class="tw-flex tw-rounded-full tw-h-2 tw-bg-gray-200 tw-w-[80px]"
          >
            <div
              :style="{ width: attachment.uploadProgress + '%' }"
              class="tw-rounded-full tw-bg-sf-primary-light"
            ></div>
          </div>
          <div
            class="
              tw-flex tw-w-[30px] tw-text-[11px] tw-font-medium tw-justify-end
            "
          >
            {{ attachment.uploadProgress }}%
          </div>
        </div>
        <div
          v-if="attachment.size"
          class="tw-mr-2 tw-text-[11px] tw-whitespace-nowrap"
          :class="{ 'tw-text-neutral-500': attachment.uploadId }"
        >
          {{ formatBytes(attachment.size) }}
        </div>
        <AttachmentListItemActions
          v-if="!attachment.uploadId"
          :attachment="attachment"
          :changes-are-disabled="changesAreDisabled"
          :show-delete-button="!singleAttachment"
          @delete-attachment="emit('deleteAttachment', attachment)"
        />
      </div>
    </div>
    <div
      v-if="
        wasAutoLinked &&
        (!attachment.previewable || singleAttachment || previewOpened)
      "
      ref="autoLinkedMessage"
      class="tw-text-sf-primary-dark tw-text-xs tw-pl-3 tw-pr-3 tw-pt-1 tw-pb-3"
    >
      <i class="fa fa-info-circle"></i>
      {{ I18n.t('file_attachments_sidebar.auto_linked_message') }}
    </div>
    <div
      v-if="attachment.previewable"
      class="attachment-preview-container"
      :style="{
        height: singleAttachment || previewOpened ? `${previewHeight}` : '0',
      }"
    >
      <PDFTron
        v-if="previewDocs.length > 0"
        :key="attachment.document_id"
        class="
          tw-border-solid
          tw-border-t
          tw-border-b-0
          tw-border-x-0
          tw-border-sf-snow-dark
          tw-h-full
        "
        :initial-docs="previewDocs"
        :path="documentPreviewItem?.pdftronPath"
        :enable-annotations="true"
        :focus-annotation-from-url="false"
        :show-annotation-bubbles="false"
        :show-communication-toggle="false"
        owner-type="Document"
        :owner-id="attachment.document_id"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Attachment } from './types'
import type { DocumentPreviewResponse } from '@app/shared/document-preview/types'

import { computed, nextTick, ref, onMounted, watch } from 'vue'

import { formatBytes } from '@app/utils/file-helpers'
import useI18n from '@app/shared/vue/composables/use-I18n'
import { isUsingNewLayout } from '@app/adapters/silverfin'

import AttachmentListItemActions from './attachment-list-item-actions.vue'
import { generateStorageKey } from './utils'
import { documentPreviewToDocs } from '@app/shared/document-preview/document-preview-to-docs'
import PDFTron from '@app/application/document/sections/pdftron.vue'
import { useDrawer } from '@app/shared/pinia/drawer'
import { RightDrawerContent } from '@app/shared/types/drawer'

import { useSidebarFileAttachmentsStore } from './store/sidebar-file-attachments'
import { storeToRefs } from 'pinia'

interface Props {
  attachment: Attachment
  changesAreDisabled: boolean
  previewHeight: string
  previewOpened?: boolean
  singleAttachment?: boolean
  documentPreview?: DocumentPreviewResponse
}

const {
  attachment,
  changesAreDisabled,
  previewHeight,
  previewOpened,
  singleAttachment,
  documentPreview,
} = defineProps<Props>()

const emit =
  defineEmits<{
    deleteAttachment: [attachment: Attachment]
  }>()

const I18n = useI18n()

const outerContainer = ref<HTMLElement | null>(null)
const autoLinkedMessage = ref<HTMLElement | null>(null)

const store = useSidebarFileAttachmentsStore()
const { togglePreview, openPreview, fetchDocumentPreview } = store
const {
  autoLinkedAttachmentKeys,
  documentPreviewPerDocumentId,
  hasOnePreviewableDocument,
} = storeToRefs(store)

const usingNewLayout = isUsingNewLayout()

const documentPreviewItem = computed(() => {
  return (
    documentPreview ||
    documentPreviewPerDocumentId.value[attachment.document_id]
  )
})

const previewDocs = computed<string[]>(() =>
  documentPreviewToDocs(documentPreviewItem.value)
)

const wasAutoLinked = computed(() => {
  return autoLinkedAttachmentKeys.value.has(generateStorageKey(attachment))
})

function handleAutoLinked() {
  if (attachment.previewable) {
    openPreview(attachment.document_id)
    fetchDocumentPreview(attachment.document_id)
  }
  nextTick(() => {
    autoLinkedMessage.value?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    })
  })
}

watch(wasAutoLinked, (value) => {
  if (value) {
    handleAutoLinked()
  }
})

const openPreviewForSingleItem = async () => {
  if (!hasOnePreviewableDocument.value && !singleAttachment) {
    return
  }

  if (!previewOpened) {
    openPreview(attachment.document_id)
  }

  await fetchDocumentPreview(attachment.document_id)
  scrollToPreview()
}

const drawerStore = useDrawer()
const { open, rightContent } = storeToRefs(drawerStore)

onMounted(() => {
  if (wasAutoLinked.value) {
    handleAutoLinked()
  }
  openPreviewForSingleItem()
})

// When the drawer is opened, check if we need to open the preview of the only previewable document
watch(open, (newDrawerState) => {
  if (newDrawerState.content !== RightDrawerContent.FileAttachments) {
    return
  }

  openPreviewForSingleItem()
})

watch(rightContent, (newValue) => {
  if (!usingNewLayout || newValue !== RightDrawerContent.FileAttachments) {
    return
  }

  openPreviewForSingleItem()
})

function scrollToPreview() {
  if (outerContainer.value) {
    outerContainer.value.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }
}

async function handleToggleAttachmentPreview(attachment: Attachment) {
  togglePreview(attachment.document_id)

  // Don't fetch/scroll if the preview got closed or
  // if the preview is not previewable
  if (!previewOpened && attachment.previewable) {
    await fetchDocumentPreview(attachment.document_id)
    scrollToPreview()
  }
}
</script>

<style scoped lang="scss">
@import 'silverfin-ui-kit/src/scss/tooling';

.attachment-list__item {
  box-shadow: #{-color('neutral', 'lighter-2')} 0 1px 0 0;

  .fa-chevron-right {
    transition: transform 0.2s ease;
  }

  &--open {
    .fa-chevron-right {
      transform: rotate(90deg);
    }
  }
}

.attachment-preview-container {
  background-color: var(--pdftron-document-background-color);
}
</style>
